import { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { ProviderSidebar } from '@/components/dashboard/ProviderSidebar';
import { Button } from '@/components/ui/button';
import { Menu, Bell } from 'lucide-react';
import { TrialBanner } from '@/components/subscription/TrialBanner';
import { TrialExpiredModal } from '@/components/subscription/TrialExpiredModal';
import { TutorialProvider } from '@/components/tutorial/TutorialProvider';
import { useOnboardingStatus } from '@/hooks/useOnboardingStatus';
import { useSubscription } from '@/hooks/useSubscription';

export function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { subscription, trialDaysRemaining, isLoading } = useSubscription();

  useOnboardingStatus();

  useEffect(() => {
    if (isLoading) return;
    if (subscription === undefined) return;

    const isActiveTrialing =
      subscription?.status === 'trialing' &&
      trialDaysRemaining > 0;

    const hasAccess =
      subscription?.status === 'active' || isActiveTrialing;

    const needsPayment = subscription !== null && !hasAccess;
    const alreadyOnSubscriptionPage = location.pathname.startsWith('/settings/subscription');

    if (needsPayment && !alreadyOnSubscriptionPage) {
      navigate('/settings/subscription', { replace: true });
    }
  }, [subscription, trialDaysRemaining, isLoading, location.pathname, navigate]);

  return (
    <TutorialProvider>
      <div className="min-h-screen bg-[#FAFAFA] flex">
        <div className="hidden xl:block xl:w-64 xl:fixed xl:inset-y-0 xl:z-50">
          <ProviderSidebar isOpen={true} onClose={() => {}} />
        </div>
        <div className="xl:hidden">
          <ProviderSidebar isOpen={sidebarOpen} onClose={() => setSidebarOpen(false)} />
        </div>
        <div className="flex-1 xl:ml-64 min-h-screen">
          <div
            className="xl:hidden sticky top-0 z-40 bg-white px-3 h-12 flex items-center justify-between"
            style={{ borderBottom: '1px solid rgba(184,134,11,0.06)' }}
          >
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-[#B8860B] h-9 w-9"
            >
              <Menu className="h-5 w-5" />
            </Button>
            <Link to="/dashboard" className="absolute left-1/2 -translate-x-1/2">
              <span
                className="font-heading text-lg text-[#2B2520] tracking-tight"
                style={{ fontFamily: "'Libre Baskerville', serif" }}
              >
                BeautyBook<span className="text-[#B8860B]">AI</span>
              </span>
            </Link>
            <Link to="/dashboard" className="text-gray-400 hover:text-[#B8860B] p-1.5">
              <Bell className="h-5 w-5" />
            </Link>
          </div>
          <TrialBanner />
          <TrialExpiredModal />
          <Outlet />
        </div>
      </div>
    </TutorialProvider>
  );
}
